/**
 * Diálogo do vínculo de trabalhador com a frente (#365, D5): vincula um funcionário elegível com
 * a data de entrada, ou corrige as datas de um vínculo existente. Quem já tem vínculo aberto em
 * outra frente aparece marcado: pode ser vinculado, mas a pessoa vê onde ele já está.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { workFrontService } from '../../../services/workFrontService';
import type { WorkFrontEligibleEmployee, WorkFrontWorkerItem } from '../../../types/workFront';
import { todayIso } from '../../fire-equipments/fireDates';

const TOUCH_TARGET = 44;

export interface WorkerDialogProps {
  open: boolean;
  frontId: number;
  /** Funcionários que podem ser vinculados (sem vínculo aberto nesta frente). */
  eligible: readonly WorkFrontEligibleEmployee[];
  /** Com um vínculo, o diálogo corrige as datas em vez de vincular. */
  worker?: WorkFrontWorkerItem;
  onClose: () => void;
  onSaved: () => void;
}

interface WorkerFieldErrors {
  employee?: string;
  entryDate?: string;
  exitDate?: string;
}

const employeeLabel = (employee: WorkFrontEligibleEmployee): string => {
  const base = employee.occupation ? `${employee.name} · ${employee.occupation}` : employee.name;
  return employee.inOtherFront ? `${base} — já está em outra frente` : base;
};

interface WorkerFormProps extends Omit<WorkerDialogProps, 'open'> {
  titleId: string;
}

const WorkerForm: React.FC<WorkerFormProps> = ({ frontId, eligible, worker, onClose, onSaved, titleId }) => {
  const editing = Boolean(worker);
  const [employeeId, setEmployeeId] = useState('');
  const [entryDate, setEntryDate] = useState(worker?.entryDate ?? todayIso());
  const [exitDate, setExitDate] = useState(worker?.exitDate ?? '');
  const [fieldErrors, setFieldErrors] = useState<WorkerFieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const found: WorkerFieldErrors = {};
    if (!editing && !employeeId) found.employee = 'Escolha o funcionário';
    if (!entryDate) found.entryDate = 'A data de entrada é obrigatória';
    if (entryDate && exitDate && exitDate < entryDate) found.exitDate = 'A saída não pode ser antes da entrada';
    setFieldErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      if (worker) {
        await workFrontService.updateWorker(frontId, worker.id, { entryDate, exitDate: exitDate || null });
      } else {
        await workFrontService.addWorker(frontId, {
          employeeId: Number(employeeId),
          entryDate,
          exitDate: exitDate || null,
        });
      }
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Não foi possível gravar o vínculo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <DialogTitle id={titleId} component="h2">
        {editing ? 'Corrigir vínculo' : 'Vincular trabalhador'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, pt: 1 }}>
          {worker ? (
            <Typography variant="body1" sx={{ gridColumn: { sm: '1 / -1' } }}>
              {worker.employee.name}
            </Typography>
          ) : (
            <TextField
              select
              label="Funcionário"
              required
              autoFocus
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
              error={Boolean(fieldErrors.employee)}
              helperText={fieldErrors.employee}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            >
              {eligible.map((employee) => (
                <MenuItem key={employee.id} value={String(employee.id)} sx={{ minHeight: TOUCH_TARGET }}>
                  {employeeLabel(employee)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Entrada"
            type="date"
            required
            value={entryDate}
            onChange={(event) => setEntryDate(event.target.value)}
            error={Boolean(fieldErrors.entryDate)}
            helperText={fieldErrors.entryDate}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Saída"
            type="date"
            value={exitDate}
            onChange={(event) => setExitDate(event.target.value)}
            error={Boolean(fieldErrors.exitDate)}
            helperText={fieldErrors.exitDate ?? 'Deixe em branco enquanto ele estiver na frente.'}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-start', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={submitting} sx={{ minHeight: TOUCH_TARGET }}>
          {editing ? 'Salvar correção' : 'Vincular trabalhador'}
        </Button>
        <Button onClick={onClose} disabled={submitting} sx={{ minHeight: TOUCH_TARGET }}>
          Cancelar
        </Button>
      </DialogActions>
    </Box>
  );
};

export const WorkerDialog: React.FC<WorkerDialogProps> = ({ open, onClose, ...rest }) => {
  const titleId = 'vinculo-titulo';
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <WorkerForm {...rest} onClose={onClose} titleId={titleId} />
    </Dialog>
  );
};

export default WorkerDialog;
